/* TCE MULTI-DOMAIN: intentional cross-root cart-transfer proof. */
(() => {
  'use strict';

  const HASH_PREFIX = '#tce-cart=';
  const MAX_FRAGMENT_LENGTH = 7000;
  const MAX_ITEMS = 100;
  const BLOCKED_PROPERTY_KEY = /(email|customer|address|phone|payment|checkout|token|secret|password)/i;
  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const cartUrl = (path) => path === 'js' ? `${root()}cart.js` : `${root()}cart/${path}`;

  const refreshCartUI = (cart) => {
    const itemCount = Number(cart?.item_count || 0);
    const displayCount = itemCount < 100 ? String(itemCount) : '99+';
    document.querySelectorAll('m-cart-count').forEach((count) => {
      count.textContent = displayCount;
      count.classList.toggle('m:hidden', itemCount === 0);
    });
    document.querySelectorAll('.m-cart-icon-bubble').forEach((link) => {
      link.setAttribute('aria-label', link.tagName === 'M-CART-TRIGGER' ? String(itemCount) : `${itemCount} items in cart`);
    });
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  };

  const requestJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) },
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  };

  const filterProperties = (properties) => {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return undefined;
    const safe = {};
    Object.entries(properties).slice(0, 25).forEach(([key, value]) => {
      if (!BLOCKED_PROPERTY_KEY.test(key) && typeof value === 'string' && key.length <= 80 && value.length <= 500) safe[key] = value;
    });
    return Object.keys(safe).length ? safe : undefined;
  };

  const encodePayload = (payload) => {
    const bytes = new TextEncoder().encode(JSON.stringify(payload));
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const decodePayload = (encoded) => {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  };

  const validDigits = (value) => /^\d+$/.test(String(value));

  const validatePayload = (payload) => {
    if (!payload || payload.version !== 1 || typeof payload.id !== 'string' || !Array.isArray(payload.items)) throw new Error('Invalid cart-transfer payload');
    if (!payload.items.length || payload.items.length > MAX_ITEMS) throw new Error('Invalid cart-transfer item count');
    const items = payload.items.map((item) => {
      if (!validDigits(item.id) || !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 999) throw new Error('Invalid cart-transfer item');
      const clean = { id: String(item.id), quantity: item.quantity };
      const properties = filterProperties(item.properties);
      if (properties) clean.properties = properties;
      if (item.selling_plan != null) {
        if (!validDigits(item.selling_plan)) throw new Error('Invalid selling plan');
        clean.selling_plan = String(item.selling_plan);
      }
      return clean;
    });
    return { id: payload.id, items };
  };

  const makePayload = (cart) => ({
    version: 1,
    id: `${Date.now().toString(36)}-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
    items: cart.items.map((item) => {
      const entry = { id: String(item.id), quantity: item.quantity };
      const properties = filterProperties(item.properties);
      if (properties) entry.properties = properties;
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlan) entry.selling_plan = String(sellingPlan);
      return entry;
    }),
  });

  const destinationWithPayload = (href, payload) => {
    const url = new URL(href, window.location.href);
    url.hash = `tce-cart=${encodePayload(payload)}`;
    return url.toString();
  };

  const destinationForCurrentPage = (href) => {
    const url = new URL(href, window.location.href);
    if (document.body.classList.contains('template-cart')) {
      url.pathname = `${root()}cart`.replace(/\/{2,}/g, '/');
    }
    return url.toString();
  };

  const handleSwitch = async (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('[data-tce-storefront-link]');
    if (!link) return;
    if (link.getAttribute('aria-current') === 'page') {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    const href = destinationForCurrentPage(link.href);
    try {
      const cart = await requestJSON(cartUrl('js'));
      if (!cart.items?.length) {
        window.location.assign(href);
        return;
      }
      const destination = destinationWithPayload(href, makePayload(cart));
      if (destination.length > MAX_FRAGMENT_LENGTH) throw new Error('Cart is too large for browser transfer');
      window.location.assign(destination);
    } catch (error) {
      console.warn('TCE MULTI-DOMAIN cart transfer skipped:', error);
      window.setTimeout(() => window.location.assign(href), 1200);
    }
  };

  const processIncomingTransfer = async () => {
    if (!window.location.hash.startsWith(HASH_PREFIX)) return;
    const encoded = window.location.hash.slice(HASH_PREFIX.length);
    history.replaceState(null, document.title, `${window.location.pathname}${window.location.search}`);
    try {
      if (!encoded || encoded.length > MAX_FRAGMENT_LENGTH) throw new Error('Invalid cart-transfer length');
      const payload = validatePayload(decodePayload(encoded));
      const storageKey = `tce-cart-transfer:${payload.id}`;
      if (sessionStorage.getItem(storageKey)) return;
      sessionStorage.setItem(storageKey, 'processing');
      const previousCart = await requestJSON(cartUrl('js'));
      const previousItems = (previousCart.items || []).map((item) => {
        const entry = { id: String(item.id), quantity: item.quantity };
        const properties = filterProperties(item.properties);
        if (properties) entry.properties = properties;
        const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;
        if (sellingPlan) entry.selling_plan = String(sellingPlan);
        return entry;
      });
      await requestJSON(cartUrl('clear.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      try {
        await requestJSON(cartUrl('add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: payload.items }),
        });
      } catch (addError) {
        if (previousItems.length) {
          await requestJSON(cartUrl('add.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: previousItems }),
          });
        }
        throw addError;
      }
      sessionStorage.setItem(storageKey, 'complete');
      refreshCartUI(await requestJSON(cartUrl('js')));
    } catch (error) {
      console.error('TCE MULTI-DOMAIN cart transfer failed:', error);
    }
  };

  const init = () => {
    document.addEventListener('click', handleSwitch);
    processIncomingTransfer();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
